/**
 * Planapus pilot landing page at /pilot.
 *
 * Composes the Oatmeal-styled pilot:
 *   - PilotInviteGate (invite-only gate, Oatmeal-styled)
 *   - Pilot: PilotNavbar + PilotBody (the Oatmeal-composed sim surface)
 *   - PilotAbout + PilotBottomCTA + PilotFooter
 *
 * The pilot body is plain React JSX composed from Oatmeal components; the
 * vanilla-JS sim engine (`lib/pilot/*.js`) binds against IDs preserved
 * in PilotBody.tsx. See Pilot.tsx for the engine contract inventory.
 */

import React, {useEffect, useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {useOsDarkMode} from '../hooks/useOsDarkMode';
import {
  initPilotTracking,
  teardownPilotTracking,
  flushPilotTracking,
} from '../lib/pilotTracking';
import {installPilotBridge, uninstallPilotBridge} from '../lib/pilot/leadsBridge';
import '../styles/oatmeal.css';
import '../styles/pilot-engine.css';
import Pilot from '../components/pilot/Pilot';
import {PilotProvider} from '../components/pilot/PilotContext';
import PilotAbout from '../components/pilot/PilotAbout';
import PilotBottomCTA from '../components/pilot/PilotBottomCTA';
import PilotFooter from '../components/pilot/PilotFooter';
import PilotInviteGate from '../components/pilot/PilotInviteGate';

// Oatmeal type stack: Inter for body, Mona Sans for display.
// Both are also linked from public/index.html via Google Fonts; the
// @fontsource imports below are the offline / build-time fallback.
// Mona Sans has no @fontsource package — Google Fonts only.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

export default function PilotLandingPage() {
  const apolloClient = useApolloClient();
  const [bridgeReady, setBridgeReady] = useState(false);
  const [bridgeError, setBridgeError] = useState<string | null>(null);

  useDocumentTitle('Pilot');
  useOsDarkMode();

  useEffect(() => {
    initPilotTracking(apolloClient);
    // installPilotBridge wires up the Apollo bridge the engine uses to
    // post leads. A failure here means the engine could load but
    // lead-capture would no-op silently — surface it instead.
    let bridgeInstalled = false;
    try {
      installPilotBridge(apolloClient);
      bridgeInstalled = true;
      setBridgeReady(true);
    } catch (err) {
      console.error('[pilot] Failed to install Apollo bridge:', err);
      setBridgeError(
        err instanceof Error ? err.message : 'failed to install pilot bridge',
      );
    }

    // The favicon-scrub and scrolled-past-hero listener moved into
    // Pilot.tsx so they're scoped to the post-gate page that owns the
    // hero. Before that move they fired against the invite-gate
    // screen too, where there's no hero to scrub a favicon for or a
    // scroll position to react to.

    return () => {
      void flushPilotTracking();
      teardownPilotTracking();
      if (bridgeInstalled) uninstallPilotBridge();
    };
  }, [apolloClient]);

  return (
    <PilotInviteGate>
      {/* F17 Slice D: PilotProvider moved up to the page level so
          PilotAbout / PilotBottomCTA / PilotFooter can read viewMode
          for their own visibility instead of being CSS-gated by the
          body class. */}
      <PilotProvider>
        <Pilot bridgeInstalled={bridgeReady} bridgeError={bridgeError} />
        <PilotAbout />
        <PilotBottomCTA />
        <PilotFooter />
      </PilotProvider>
    </PilotInviteGate>
  );
}
